/**
 * Mini Cart Header
 * ────────────────────────────────────────────────────────────────────────
 * Drawer interactions for header-integrated mini-cart section.
 * Handles: open/close, keyboard controls, focus management, form
 * interception, note auto-save.
 *
 * Accessibility (ARIA dialog pattern): the closed drawer is inert and
 * aria-hidden, opening moves focus to the Close button and traps Tab inside
 * the drawer, closing restores focus to the element that opened it.
 * html.mini-cart-open is set while the drawer is open so the section CSS can
 * hide the chat launchers that would otherwise cover the footer buttons.
 */
class MiniCartHeader {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.root = document.getElementById(`MiniCart-${sectionId}`);
    if (!this.root) return;

    this.drawer = this.root.querySelector('[data-mini-cart-drawer]');
    this.backdrop = this.root.querySelector('[data-mini-cart-backdrop]');
    this.noteTimer = null;
    this.lastTrigger = null;

    this._bindEvents();
    this._setupDeliveryDates();
  }

  isOpen() {
    return this.root.classList.contains('drawer-open');
  }

  open() {
    if (this.isOpen()) return;
    const active = document.activeElement;
    this.lastTrigger = active && active !== document.body ? active : null;

    this.root.classList.add('drawer-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.removeAttribute('inert');
    document.documentElement.classList.add('mini-cart-open');

    // Move focus into the dialog once it is visible (visibility flips with the class)
    requestAnimationFrame(() => {
      if (!this.isOpen()) return;
      const closeBtn = this.drawer.querySelector('[data-mini-cart-close]');
      const target = closeBtn || this.drawer;
      try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
    });
  }

  close() {
    if (!this.isOpen()) return;
    const focusWasInside = this.drawer.contains(document.activeElement);

    this.root.classList.remove('drawer-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    document.documentElement.classList.remove('mini-cart-open');

    if (focusWasInside || document.activeElement === document.body) {
      this._restoreFocus();
    }
    this.lastTrigger = null;
  }

  _restoreFocus() {
    let target = this.lastTrigger;
    if (!target || !document.contains(target) || target.closest('[inert]') || target.disabled || target.getClientRects().length === 0) {
      target = Array.from(document.querySelectorAll('[data-mini-cart-trigger]')).find((el) => el.getClientRects().length > 0) || null;
    }
    if (!target) return;
    try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
  }

  _focusableInDrawer() {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    // getClientRects() is empty for display:none nodes and for the content of a closed <details>
    return Array.from(this.drawer.querySelectorAll(selector)).filter((el) => {
      return el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0;
    });
  }

  _trapTab(e) {
    if (e.key !== 'Tab' || !this.isOpen()) return;
    const focusable = this._focusableInDrawer();
    if (!focusable.length) {
      e.preventDefault();
      this.drawer.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const inside = this.drawer.contains(active);

    if (e.shiftKey) {
      if (!inside || active === first || active === this.drawer) {
        e.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _bindEvents() {
    // Trigger buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mini-cart-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });

    // Close button
    this.root.addEventListener('click', (e) => {
      if (e.target.closest('[data-mini-cart-close]')) {
        e.preventDefault();
        this.close();
      }
    });

    // Backdrop click
    this.backdrop.addEventListener('click', () => this.close());

    // Escape key closes, Tab stays inside the dialog
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      this._trapTab(e);
    });

    // Cart item added event: prevent the /cart redirect and open the drawer.
    // theme.js only dispatches this after a successful /cart/add.js; a rejected add
    // dispatches cart:add-failed instead, which leaves the drawer closed.
    document.addEventListener('cart:item-added', (e) => {
      e.preventDefault();
      this.open();
      this._refreshDeliveryDates();
    });

    // Note textarea auto-save
    this.root.addEventListener('input', (e) => {
      const noteField = e.target.closest('[data-mini-cart-note]');
      if (!noteField) return;

      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        if (window.cart && typeof window.cart.updateNote === 'function') {
          window.cart.updateNote(noteField.value);
        }
      }, 600);
    });
  }

  _refreshDeliveryDates() {
    if (typeof refreshAllDeliveryDates === 'function') refreshAllDeliveryDates();
  }

  _setupDeliveryDates() {
    if (document.readyState !== 'loading') {
      setTimeout(() => this._refreshDeliveryDates(), 100);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this._refreshDeliveryDates(), 100);
      });
    }
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this._refreshDeliveryDates(), 150);
    });

    document.addEventListener('cart:refresh-delivery-dates', () => {
      this._refreshDeliveryDates();
    });
    setInterval(() => {
      if (typeof updateAllShipTodayCountdowns === 'function') updateAllShipTodayCountdowns();
    }, 60000);
  }
}

// Bootstrap (idempotent)
if (!window.miniCartHeader) {
  document.addEventListener('DOMContentLoaded', function init() {
    const miniCartEl = document.querySelector('[data-section-id][id^="MiniCart-"]');
    if (miniCartEl) {
      const sectionId = miniCartEl.getAttribute('data-section-id');
      window.miniCartHeader = new MiniCartHeader(sectionId);
      document.removeEventListener('DOMContentLoaded', init);
    }
  });

  // Fallback if DOMContentLoaded already fired
  if (document.readyState !== 'loading') {
    const miniCartEl = document.querySelector('[data-section-id][id^="MiniCart-"]');
    if (miniCartEl) {
      const sectionId = miniCartEl.getAttribute('data-section-id');
      window.miniCartHeader = new MiniCartHeader(sectionId);
    }
  }
}
